import moment from 'moment';
import type { ExistingFile, NewFile } from '../../components/shared/FileUpload/FileUpload';
import type { TaskAttachment, TaskProject, TaskStatus } from '../../services/service-tasks';

export type Option<V extends string = string> = { value: V; label: string };

export type ActivityDraft = {
  title: string;
  description: string;
  dueDate: Date | null;
  projectId: string | null;
  assignTo: string[];
  status: TaskStatus;
  existingFiles: ExistingFile[];
  newFiles: NewFile[];
};

export type ActivityField = 'title' | 'description' | 'dueDate' | 'projectId' | 'assignTo';

export const validateActivity = (draft: ActivityDraft): ActivityField[] => {
  const missing: ActivityField[] = [];
  if (!draft.title.trim()) missing.push('title');
  if (!draft.description.trim()) missing.push('description');
  if (!draft.dueDate) missing.push('dueDate');
  if (!draft.projectId) missing.push('projectId');
  if (draft.assignTo.length === 0) missing.push('assignTo');
  return missing;
};

export const dueDateFromPick = (current: Date | null, picked: Date): Date =>
  current && moment(current).isSame(picked, 'day')
    ? current
    : moment(picked).startOf('day').toDate();

export const toTaskFormData = (draft: ActivityDraft, taskId?: string): FormData => {
  const formData = new FormData();
  formData.append('title', draft.title);
  formData.append('description', draft.description);
  formData.append('projectId', draft.projectId ?? '');
  draft.assignTo.forEach(id => formData.append('assignTo', id));
  if (draft.dueDate) formData.append('dueDate', draft.dueDate.toISOString());
  formData.append('status', draft.status);
  formData.append(
    'attachments',
    JSON.stringify(
      draft.existingFiles.map(f => ({
        fileId: f.file,
        fileName: f.fileName,
        mimeType: f.mimeType,
      })),
    ),
  );
  draft.newFiles.forEach(f => formData.append(f.key, f.file));
  if (taskId) formData.append('taskId', taskId);
  return formData;
};

export const toExistingFiles = (attachments: TaskAttachment[]): ExistingFile[] =>
  attachments.map(a => ({ file: a.fileId, fileName: a.fileName, mimeType: a.mimeType }));

export const toProjectOptions = (projects: TaskProject[]): Option[] => {
  const named = projects.filter((p): p is TaskProject & { name: string } => Boolean(p.name));
  const nameCounts = new Map<string, number>();
  named.forEach(p => nameCounts.set(p.name, (nameCounts.get(p.name) ?? 0) + 1));
  return named.map(p => ({
    value: p._id,
    label: p.number && (nameCounts.get(p.name) ?? 0) > 1 ? `${p.name} (${p.number})` : p.name,
  }));
};
